/**
 * Product Subscription Service
 * Handles product subscription management and limit checking
 */

// Use environment variable or default to production URL
// In local development, API calls will be skipped if CORS issues occur
const API_BASE_URL = process.env.REACT_APP_PRODUCT_SUBSCRIPTION_API_URL || 'https://bookapt-product-subscriptions.onrender.com';

export interface ProductSubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  productLimit: number | 'Unlimited';
  categoryLimit: number | 'Unlimited';
  imagesPerProduct: number | 'Unlimited';
  variantsPerProduct: number | 'Unlimited';
  features: string[];
}

export interface ProductSubscriptionStatus {
  planId: string | null;
  planName: string | null;
  status: string;
  currentPeriodEnd: string | null;
  productLimit: number;
  activeProducts: number;
  canAddProduct: boolean;
  remainingProducts: number | 'Unlimited';
}

export interface ProductLimitCheck {
  success: boolean;
  canAdd: boolean;
  activeProducts: number;
  limit: number;
  remaining: number;
  message: string;
}

class ProductSubscriptionService {
  /**
   * Get available subscription plans
   */
  async getPlans(): Promise<ProductSubscriptionPlan[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/product-subscriptions/plans`);
      const data = await response.json();
      
      if (data.success) {
        return data.plans;
      }
      throw new Error(data.error || 'Failed to get plans');
    } catch (error) {
      console.error('Error getting subscription plans:', error);
      throw error;
    }
  }

  /**
   * Get subscription status for a business
   */
  async getStatus(businessId: string): Promise<ProductSubscriptionStatus> {
    // Skip API call in local development if API URL is not accessible
    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    if (!API_BASE_URL || API_BASE_URL.includes('localhost') || isLocalhost) {
      // Return default free plan status for local development
      return {
        planId: 'free',
        planName: 'Product Free',
        status: 'active',
        currentPeriodEnd: null,
        productLimit: 10,
        activeProducts: 0,
        canAddProduct: true,
        remainingProducts: 10,
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/product-subscriptions/status/${businessId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return data.subscription;
      }
      throw new Error(data.error || 'Failed to get subscription status');
    } catch (error) {
      // Silently return default free plan status on error (CORS, network, etc.)
      if (__DEV__) {
        console.warn('Error getting subscription status (using default free plan):', error);
      }
      // Return default free plan status on error
      return {
        planId: 'free',
        planName: 'Product Free',
        status: 'active',
        currentPeriodEnd: null,
        productLimit: 10,
        activeProducts: 0,
        canAddProduct: true,
        remainingProducts: 10,
      };
    }
  }

  /**
   * Check if business can add more products
   */
  async checkLimit(businessId: string): Promise<ProductLimitCheck> {
    // Skip API call in local development if API URL is not accessible
    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    if (!API_BASE_URL || API_BASE_URL.includes('localhost') || isLocalhost) {
      // Return permissive default for local development
      return {
        success: false,
        canAdd: true,
        activeProducts: 0,
        limit: 999,
        remaining: 999,
        message: 'Local development mode - limit checking disabled',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/product-subscriptions/check-limit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessId }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return {
          success: true,
          canAdd: data.canAdd,
          activeProducts: data.activeProducts,
          limit: data.limit,
          remaining: data.remaining,
          message: data.message,
        };
      }
      throw new Error(data.error || 'Failed to check limit');
    } catch (error) {
      // Silently return permissive default on error (CORS, network, etc.)
      if (__DEV__) {
        console.warn('Error checking product limit (allowing operation):', error);
      }
      // Return permissive default on error - don't block user operations
      return {
        success: false,
        canAdd: true,
        activeProducts: 0,
        limit: 999,
        remaining: 999,
        message: 'Unable to check limit, proceeding anyway',
      };
    }
  }

  /**
   * Increment product count (call after adding a product)
   * @param businessId - Business ID
   * @param count - Number of products to increment (default: 1)
   */
  async incrementCount(businessId: string, count: number = 1): Promise<void> {
    // Skip if API URL is not configured or running on localhost (local development)
    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    if (!API_BASE_URL || API_BASE_URL.includes('localhost') || isLocalhost) {
      // Silently skip in local development - no need to log
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/product-subscriptions/increment-count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessId, count }),
      });
      
      if (!response.ok) {
        // Only log in development, silently fail in production
        if (__DEV__) {
          console.warn(`Failed to increment product count: HTTP ${response.status}`);
        }
        return;
      }
    } catch (error) {
      // Silently fail - product count tracking is not critical for core functionality
      // Only log in development mode
      if (__DEV__) {
        console.warn('Error incrementing product count (non-critical, continuing):', error);
      }
      // Don't throw - this is not critical and shouldn't block product creation
    }
  }

  /**
   * Decrement product count (call after removing/deactivating a product)
   */
  async decrementCount(businessId: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/product-subscriptions/decrement-count`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ businessId }),
      });
    } catch (error) {
      console.error('Error decrementing product count:', error);
      // Don't throw - this is not critical
    }
  }

  /**
   * Create subscription (for paid plans)
   */
  async createSubscription(
    businessId: string,
    planId: string,
    businessEmail: string,
    businessName: string
  ): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/product-subscriptions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId,
          businessId,
          businessEmail,
          businessName,
        }),
      });
      const data = await response.json();
      
      if (data.success) {
        return data;
      }
      throw new Error(data.error || 'Failed to create subscription');
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }
}

export default new ProductSubscriptionService();

